import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "近期活動", href: "#upcoming-events", isPage: false },
  { label: "活動分類", href: "#activity-categories", isPage: false },
  { label: "精彩回顧", href: "#memories", isPage: false },
  { label: "關於我們", href: "#about-us", isPage: false },
  { label: "工商合作", href: "/business", isPage: true },
  { label: "直播主招募", href: "/recruit", isPage: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string, isPage: boolean) => {
    setMenuOpen(false);
    if (isPage) {
      navigate(href);
    } else {
      if (isHomePage) {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        // 不在首頁時，先跳回首頁再捲動
        navigate("/");
        setTimeout(() => {
          const el = document.querySelector(href);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/2de05ae8-ab7f-40ca-afb5-96f93dc606a2.png"
            alt="Rappy me Logo"
            className="h-10 w-10 object-contain"
          />
          <span
            className={`font-bold text-lg whitespace-nowrap transition-colors duration-300 ${
              scrolled ? "text-gray-900" : "text-white"
            }`}
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            娛樂紅歡密
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href, link.isPage)}
              className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap relative group ${
                link.isPage
                  ? scrolled
                    ? "text-red-600 hover:text-red-700"
                    : "text-red-300 hover:text-white"
                  : scrolled
                  ? "text-gray-700 hover:text-red-700"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => handleNavClick("#wish-pool", false)}
            className={`text-sm font-medium px-4 py-2 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              scrolled
                ? "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                : "border-white text-white hover:bg-white hover:text-red-700"
            }`}
          >
            許願池
          </button>
          <button
            onClick={() => handleNavClick("#upcoming-events", false)}
            className="text-sm font-medium px-5 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            探索活動
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
            scrolled ? "text-gray-800" : "text-white"
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-3">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href, link.isPage)}
              className={`text-left font-medium py-2 border-b border-gray-50 cursor-pointer transition-colors ${
                link.isPage ? "text-red-600 hover:text-red-700" : "text-gray-700 hover:text-red-600"
              }`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNavClick("#upcoming-events", false)}
            className="mt-2 w-full py-2.5 rounded-full bg-red-600 text-white font-medium text-sm cursor-pointer whitespace-nowrap"
          >
            探索活動
          </button>
        </div>
      )}
    </header>
  );
}
